import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  productMaterialCategoryOptions,
  productMaterialCreate,
  productMaterialList,
  productMaterialUpdate,
  productMaterialUpdateEnable,
} from 'apis/product-material';
import { getLocalStorageLanguage } from 'commons';
import { LANGUAGE_MAP } from 'constant/language';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PmsProductMaterialCategoryOption,
  PmsProductMaterialRecord,
} from 'types/pms-product-material';

type MaterialTreeNode = PmsProductMaterialRecord & {
  children?: MaterialTreeNode[];
};

type MaterialFormValues = {
  value: string;
  sort: number;
  labelCn: string;
  labelEn: string;
  labelJa: string;
  labelTw?: string;
};

const categoryLabel = (category: PmsProductMaterialCategoryOption) => {
  const language = getLocalStorageLanguage();
  if (language === LANGUAGE_MAP.EN) return category.name;
  if (language === LANGUAGE_MAP.JA) return category.nameJa || category.name;
  return category.nameZh || category.name;
};

const buildTree = (list: PmsProductMaterialRecord[]): MaterialTreeNode[] => {
  const nodes = new Map<number, MaterialTreeNode>();
  list.forEach((item) => {
    nodes.set(item.id, { ...item });
  });

  const roots: MaterialTreeNode[] = [];
  list.forEach((item) => {
    const node = nodes.get(item.id);
    if (!node) return;
    const parent = item.parentId ? nodes.get(item.parentId) : undefined;
    if (parent) {
      parent.children = parent.children || [];
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const nodeText = (node: MaterialTreeNode) =>
  [node.value, node.labelCn, node.labelEn, node.labelJa, node.labelTw]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const filterTree = (
  nodes: MaterialTreeNode[],
  keyword: string,
): MaterialTreeNode[] => {
  const query = keyword.trim().toLowerCase();
  if (!query) return nodes;

  return nodes.flatMap((node) => {
    if (nodeText(node).includes(query)) return [node];
    const children = filterTree(node.children || [], query);
    if (!children.length) return [];
    return [{ ...node, children }];
  });
};

const collectParentIds = (nodes: MaterialTreeNode[]): number[] => {
  const ids: number[] = [];
  nodes.forEach((node) => {
    if (node.children?.length) {
      ids.push(node.id, ...collectParentIds(node.children));
    }
  });
  return ids;
};

const ProductMaterialPage = () => {
  const [categories, setCategories] = useState<
    PmsProductMaterialCategoryOption[]
  >([]);
  const [categoryId, setCategoryId] = useState<number>();
  const [keyword, setKeyword] = useState('');
  const [list, setList] = useState<PmsProductMaterialRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<number[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<PmsProductMaterialRecord>();
  const [parent, setParent] = useState<PmsProductMaterialRecord>();
  const [togglingId, setTogglingId] = useState<number>();
  const [form] = Form.useForm<MaterialFormValues>();

  useEffect(() => {
    productMaterialCategoryOptions().then((options) => {
      setCategories(options);
      if (options[0]) setCategoryId(options[0].id);
    });
  }, []);

  const loadList = useCallback(async () => {
    if (!categoryId) {
      setList([]);
      return;
    }
    setLoading(true);
    try {
      const res = await productMaterialList(categoryId);
      setList(res.list);
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const tree = useMemo(() => buildTree(list), [list]);
  const filteredTree = useMemo(
    () => filterTree(tree, keyword),
    [tree, keyword],
  );

  useEffect(() => {
    if (!keyword.trim()) return;
    setExpandedKeys(collectParentIds(filteredTree));
  }, [keyword, filteredTree]);

  const byId = useMemo(() => {
    const map = new Map<number, PmsProductMaterialRecord>();
    list.forEach((item) => map.set(item.id, item));
    return map;
  }, [list]);

  const openCreate = (parentNode?: PmsProductMaterialRecord) => {
    setEditing(undefined);
    setParent(parentNode);
    form.resetFields();
    form.setFieldsValue({ sort: 0 });
    setOpen(true);
  };

  const openEdit = (row: PmsProductMaterialRecord) => {
    setEditing(row);
    setParent(row.parentId ? byId.get(row.parentId) : undefined);
    form.setFieldsValue({
      value: row.value,
      sort: row.sort ?? 0,
      labelCn: row.labelCn,
      labelEn: row.labelEn,
      labelJa: row.labelJa,
      labelTw: row.labelTw,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!categoryId) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      const labels = {
        sort: values.sort ?? 0,
        labelCn: values.labelCn.trim(),
        labelEn: values.labelEn.trim(),
        labelJa: values.labelJa.trim(),
        labelTw: values.labelTw?.trim() || '',
      };
      if (editing) {
        await productMaterialUpdate(editing.id, labels);
      } else {
        await productMaterialCreate({
          ...labels,
          value: values.value.trim(),
          categoryId,
          parentId: parent?.id || 0,
        });
      }
      message.success(editing ? '已保存' : '已创建');
      setOpen(false);
      loadList();
    } finally {
      setSaving(false);
    }
  };

  const toggleEnable = async (row: MaterialTreeNode, enabled: boolean) => {
    setTogglingId(row.id);
    try {
      await productMaterialUpdateEnable(row.id, enabled ? 1 : 0);
      message.success(enabled ? '已启用' : '已停用');
      await loadList();
    } finally {
      setTogglingId(undefined);
    }
  };

  const columns: ColumnsType<MaterialTreeNode> = [
    { title: '中文名', dataIndex: 'labelCn', width: 180 },
    { title: '英文名', dataIndex: 'labelEn', width: 180 },
    { title: '日文名', dataIndex: 'labelJa', width: 180 },
    { title: '繁体名', dataIndex: 'labelTw', width: 180 },
    { title: '取值', dataIndex: 'value', width: 180 },
    { title: '排序', dataIndex: 'sort', width: 80 },
    {
      title: '启用',
      width: 90,
      render: (_, row) => (
        <Switch
          checked={row.enabled}
          loading={togglingId === row.id}
          onChange={(checked) => toggleEnable(row, checked)}
        />
      ),
    },
    {
      title: '操作',
      width: 180,
      render: (_, row) => (
        <Space>
          <Button type="link" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Button type="link" onClick={() => openCreate(row)}>
            新增下级
          </Button>
        </Space>
      ),
    },
  ];

  const parentName = parent
    ? parent.labelCn || parent.labelEn || parent.value
    : '无（一级材质）';

  return (
    <Card
      title="商品材质"
      extra={
        <Button
          type="primary"
          disabled={!categoryId}
          onClick={() => openCreate()}
        >
          新建
        </Button>
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="商品大类"
          style={{ width: 240 }}
          value={categoryId}
          options={categories.map((category) => ({
            value: category.id,
            label: categoryLabel(category),
          }))}
          onChange={(value) => {
            setCategoryId(value);
            setExpandedKeys([]);
          }}
        />
        <Input
          allowClear
          placeholder="名称 / 取值"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          style={{ width: 240 }}
        />
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredTree}
        pagination={false}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpand: (expanded, record) => {
            setExpandedKeys((prev) =>
              expanded
                ? [...prev, record.id]
                : prev.filter((id) => id !== record.id),
            );
          },
        }}
      />
      <Modal
        title={editing ? '编辑材质' : parent ? '新增下级材质' : '新建材质'}
        open={open}
        confirmLoading={saving}
        onCancel={() => setOpen(false)}
        onOk={submit}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="上级">
            <Input value={parentName} disabled />
          </Form.Item>
          <Form.Item
            label="取值"
            name="value"
            extra={
              editing
                ? '取值创建后不能修改'
                : '商品按这个值保存材质，同一属性下不能重复。'
            }
            rules={
              editing
                ? []
                : [{ required: true, whitespace: true, message: '请填写取值' }]
            }
          >
            <Input maxLength={255} disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item
            label="中文名"
            name="labelCn"
            rules={[
              { required: true, whitespace: true, message: '请填写中文名' },
            ]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            label="英文名"
            name="labelEn"
            rules={[
              { required: true, whitespace: true, message: '请填写英文名' },
            ]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            label="日文名"
            name="labelJa"
            rules={[
              { required: true, whitespace: true, message: '请填写日文名' },
            ]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item label="繁体名" name="labelTw">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item label="排序" name="sort" extra="数字越大越靠前">
            <InputNumber precision={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProductMaterialPage;
